/**
 * TrendingPeek - the warm-tinted "getting noisy" card on the DQ Workbench
 * Overview. Shows the top few by-FP trending rows (highest delta first) as
 * compact rows with a market chip, label, and a tiny rate bar scaled to the
 * delta. Ends with a link into the full Trending tab (?market= preserved).
 */
import React from 'react'
import { Box, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import type { TrendingRow } from '../../../../services/admin/dataExceptionsService'
import { dqHeatBackground } from '../../../../theme/dqHeatRamp'
import { dqTrendingWarm, dqTrendingAccent } from '../../../../theme/dqStatusColors'
import MarketChip from './MarketChip'

interface Props {
  rows: TrendingRow[]
  trendingTo: string
}

const TrendingPeek: React.FC<Props> = ({ rows, trendingTo }) => {
  const top = [...rows].sort((a, b) => b.delta - a.delta).slice(0, 3)
  const maxDelta = Math.max(...top.map((r) => r.delta), 1)

  return (
    <Box>
      {top.map((row) => (
        <Box key={row.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
          <MarketChip market={row.market} />
          <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}>
            {row.label}
          </Typography>
          <Box sx={{ width: 80, height: 6, borderRadius: 1, bgcolor: dqTrendingWarm.border, overflow: 'hidden' }}>
            <Box
              sx={{
                width: `${(Math.max(row.delta, 0) / maxDelta) * 100}%`,
                height: '100%',
                bgcolor: dqHeatBackground(row.delta, maxDelta),
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ width: 36, textAlign: 'right', fontWeight: 700, color: dqTrendingAccent }}>
            +{row.delta}
          </Typography>
        </Box>
      ))}

      <Box sx={{ mt: 1.5 }}>
        <Typography
          component={Link}
          to={trendingTo}
          variant="body2"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: dqTrendingAccent,
            fontWeight: 600,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          Open Trending
          <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </Typography>
      </Box>
    </Box>
  )
}

export default TrendingPeek
